import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { ApiError } from '~/api/client'
import { useAuth } from '~/auth/auth-context'
import { reached } from '~/components/use-work'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { PanelHeader } from '~/components/panel-header'
import { Menu, MenuItem } from '~/components/editor-menu'
import { CopyIcon, DeleteIcon, EditIcon, MoreIcon, UnpublishIcon } from '~/components/icons'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { StartRunDialog } from '~/components/start-run-dialog'
import { GroupIcon } from '~/components/template-board'
import { useToast } from '~/components/toast-context'
import { useDataService, useDataState } from '~/data/data-context'
import type { LibraryTemplate, WorkoutsDone } from '~/data/templates'
import { GROUP_LABEL, GROUP_TEXT } from '~/lib/blocks'
import { DIALOG_PATHS, withoutDialog } from '~/lib/dialogs'
import { ranLabel } from '~/lib/listings'
import {
  descriptionOf,
  doneByWorkout,
  runDetail,
  runSpan,
  runTally,
  runsOf,
  shapeOf,
  workoutsDone,
  workoutsOf,
  type HeldExercise,
  type TemplateFacts,
} from '~/lib/template-page'
import { strictNode } from '~/lib/template-tree'

/**
 * A template's own page — `@specs/features/explore.spec.md`, A template's page.
 *
 * One page for every template, whoever wrote it and whether or not it is published: what the
 * program is, what you have done of it, and what you can do with it next. A template of your
 * own is read out of your library; anybody else's out of its listing, which is the only way to
 * see a template that is not yours.
 */
export function TemplatePanel({
  templateId,
  onName,
}: {
  templateId: string
  /**
   * What the template turns out to be called. The card around this is named for what it is
   * showing, and a template somebody else published is not in your library for it to read —
   * so the panel that found out says so.
   */
  onName: (name: string) => void
}) {
  const service = useDataService()
  const state = useDataState()
  const navigate = useNavigate()
  const toast = useToast()
  const { session } = useAuth()
  // Explore's filters ride along while its template's page is open, so the way back can put
  // them back — see the root route, which is what validates them.
  const { explore: narrowed } = useSearch({ strict: false })

  const [listing, setListing] = useState<LibraryTemplate | null>(null)
  /** Whether the look in Explore has come back, either way. */
  const [looked, setLooked] = useState(false)
  const [offline, setOffline] = useState(false)
  const [done, setDone] = useState<WorkoutsDone[] | null>(null)
  /** Whether the counts were asked for and did not come — unknown, rather than none. */
  const [countsMissed, setCountsMissed] = useState(false)
  const [starting, setStarting] = useState(false)
  const [unlisting, setUnlisting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  /** On the way out after a delete, so the page never turns into a page about nothing. */
  const [leaving, setLeaving] = useState(false)
  const [trouble, setTrouble] = useState<string | null>(null)

  const held = state.templates.find((template) => template.id === templateId) ?? null
  const running = state.enrollments.find((enrollment) => enrollment.endedAt === null) ?? null

  useEffect(() => {
    let alive = true
    service
      .exploreTemplate(templateId)
      .then((found) => {
        if (alive) {
          setListing(found)
          setLooked(true)
        }
      })
      .catch((error: unknown) => {
        if (alive) {
          /*
           * Unlisted and unreachable are not the same answer, and saying the first when it is
           * the second tells somebody their program is gone when their signal is. Only a 404
           * means it stands in Explore no longer.
           */
          setOffline(!(error instanceof ApiError && error.status === 404))
          setLooked(true)
        }
      })
    return () => {
      alive = false
    }
  }, [service, templateId])

  // What you have done of it, by workout. Counted by the server: this device holds the most
  // recent handful of workouts and would answer with whatever happened to be loaded.
  useEffect(() => {
    let alive = true
    service
      .templateWorkoutsDone(templateId)
      .then((found) => {
        if (alive) {
          setDone(found)
          setCountsMissed(false)
        }
      })
      .catch(() => {
        // Left unknown rather than emptied. A count that did not arrive is not a count of
        // nothing, and a page that answered "you have done none of this" because the request
        // failed would be saying something false about somebody's training. The runs
        // themselves are on the device, so they are still shown — it is only what the server
        // counts that goes unsaid.
        if (alive) {
          setCountsMissed(true)
        }
      })
    return () => {
      alive = false
    }
  }, [service, templateId, state.workouts])

  /*
   * Nothing is said about a template until there is something to say it about. The library
   * arrives with everything else the app opens with, and the look in Explore answers either
   * way — until both have settled, a page that said "no longer in Explore" would be saying it
   * about a template that is simply still on its way.
   *
   * A template you keep is settled the moment the library is: everything the page says about
   * one of your own is read out of it, and the look in Explore is a question only anybody
   * else's template needs the answer to.
   */
  /*
   * Offline is an answer, not a wait. A browser that has never read the account leaves the
   * timeline unsettled — rightly, since it cannot say what the library holds — but the look in
   * Explore has come back, and what it came back with is that there is no reaching this
   * template without a connection. Saying so beats a skeleton that will never fill.
   */
  const settled = (state.timelineSettled || offline) && (held !== null || looked)
  const facts: TemplateFacts | null =
    held !== null
      ? {
          templateId: held.id,
          name: held.name,
          script: held.script,
          description: descriptionOf(held.script),
          authorName: null,
          mine: true,
          published: held.publishedVersionId != null,
          ranCount: listing?.ranCount ?? null,
          runningCount: listing?.runningCount ?? null,
        }
      : listing !== null
        ? {
            templateId: listing.templateId,
            name: listing.name,
            script: listing.script,
            description: listing.description ?? descriptionOf(listing.script),
            authorName: listing.authorName,
            mine: listing.mine,
            published: true,
            ranCount: listing.ranCount,
            runningCount: listing.runningCount,
          }
        : null

  // What the card around this should call itself, once there is an answer.
  const name = facts?.name ?? null
  useEffect(() => {
    if (name !== null) {
      onName(name)
    }
    // The callback is the caller's to keep stable; what this effect is about is the name.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])

  /** Where the way back leads: Explore when that is where this was reached from. */
  const back =
    narrowed === undefined
      ? {
          search: { dialog: 'templates' as const },
          path: DIALOG_PATHS.templates,
          name: 'Templates',
        }
      : {
          search: { dialog: 'explore' as const, explore: narrowed },
          path: DIALOG_PATHS.explore,
          name: 'Explore',
        }

  /**
   * The card steps back to whatever this was opened from rather than closing outright, the way
   * every panel in it steps back to the menu — and whatever the page did, it lands there: a
   * run started, a template deleted, or nothing at all.
   */
  const goBack = () => {
    void navigate({
      to: '.',
      search: (held) => ({ ...withoutDialog(held), ...back.search }),
      mask: { to: back.path },
      replace: true,
      resetScroll: false,
      viewTransition: false,
    })
  }

  async function start(anchorDay: number | null): Promise<void> {
    if (facts === null) {
      return
    }
    // Your own runs what it says now; anybody else's runs the version Explore is offering.
    if (held !== null) {
      await service.enroll(held.id, anchorDay)
    } else if (listing !== null) {
      await service.enrollInListing(listing, anchorDay)
    }
    goBack()
  }

  async function copy(): Promise<void> {
    if (listing === null) {
      return
    }
    const made = await service.copyTemplate(listing)
    await navigate({ to: '/templates/$templateId/edit', params: { templateId: made.id } })
  }

  if (!settled || leaving) {
    return (
      <>
        <PanelHeader title="Template" onBack={goBack} backLabel={`Back to ${back.name}`} />
        <SkeletonBlock label="Loading the template" className="w-104 space-y-3 px-4 pb-4">
          <Skeleton className="h-9 w-56" />
          <Skeleton className="h-5 w-40" />
          <Skeleton className="mt-5 h-11 w-44 rounded-full" />
        </SkeletonBlock>
      </>
    )
  }

  if (facts === null) {
    return (
      <>
        <PanelHeader title="Template" onBack={goBack} backLabel={`Back to ${back.name}`} />
        <div className="w-104 px-4 pb-4">
          <p className="text-ink-muted">
            {offline
              ? 'Reaching a template you do not keep needs a connection.'
              : 'This template is not yours, and it is not in Explore.'}
          </p>
        </div>
      </>
    )
  }

  const runs = runsOf(state.enrollments, facts.templateId)
  const workouts = workoutsOf(facts.script)
  const counts = doneByWorkout(done ?? [])
  const onItNow = runs.some((run) => run.endedAt === null)
  const admin = session?.user.isAdmin === true

  return (
    <>
      {/*
        The header is the whole of what the template is called: its name over the shape it runs
        in. Everything the page can do to it but the one thing it is for hangs off the menu at
        the end of that line.
      */}
      <PanelHeader
        title={facts.name}
        subtitle={shapeOf(facts.script)}
        onBack={goBack}
        backLabel={`Back to ${back.name}`}
      >
        {/*
          What you have done of it, beside the name rather than under it: it answers the
          question somebody opens the page with, and reading the whole header answers it.

          Held back while the counts are still on their way, and said once they have arrived or
          turned out not to be coming — these two are the device's own knowledge, so a look that
          failed is a reason to stop waiting rather than a reason never to answer.
        */}
        {(done !== null || countsMissed) && runs.length > 0 ? (
          <div className="flex shrink-0 gap-4 pr-1 text-right">
            <Stat value={String(workoutsDone(runs))} label="Workouts" />
            <Stat value={String(runs.length)} label={runs.length === 1 ? 'Run' : 'Runs'} />
          </div>
        ) : null}
      </PanelHeader>
      <section className="w-104 px-4 pb-24">
        {/*
          Whose it is, where it is not yours, and whether anybody else can find it.

          Nothing says "private": a template of your own is private until you publish it, and a
          word every template in the library would wear says nothing about any of them. Nothing
          says you are on it either — the action below says that, and says it as the answer to
          the only question the mark was answering.
        */}
        {facts.authorName !== null || facts.published ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {facts.authorName === null ? null : (
              <p className="text-ink-muted">By {facts.authorName}</p>
            )}
            {facts.published ? (
              <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-xs text-accent">
                Public
              </span>
            ) : null}
          </div>
        ) : null}

        {facts.ranCount !== null && facts.runningCount !== null ? (
          <p className="pt-2 text-sm text-ink-muted">
            {ranLabel(facts.ranCount)} · {String(facts.runningCount)} on it now
          </p>
        ) : null}

        {facts.description !== null ? (
          <p className="pt-4 whitespace-pre-line">{facts.description}</p>
        ) : null}

        {trouble !== null ? <p className="pt-4 text-sm text-danger">{trouble}</p> : null}

        {workouts.length > 0 ? (
          /* Nothing heads the workouts — what they hold says what they are. */
          <section aria-label="What it holds" className="pt-8">
            <div className="space-y-7">
              {workouts.map((workout) => (
                <article key={workout.name}>
                  <div className="flex items-end gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{workout.name}</p>
                      {workout.day !== null ? (
                        <p className="text-sm text-ink-muted">{workout.day}</p>
                      ) : null}
                    </div>
                    {/* How many times you have done this one, once the counts are in. */}
                    {done !== null && (counts.get(workout.name) ?? 0) > 0 ? (
                      <span className="shrink-0 text-sm text-ink-muted">
                        Done {String(counts.get(workout.name) ?? 0)}{' '}
                        {counts.get(workout.name) === 1 ? 'time' : 'times'}
                      </span>
                    ) : null}
                  </div>
                  {/*
                  A card is what says which thing is inside which, and a template is the one
                  place in the app where things nest — so every block a workout holds takes one,
                  and what a group holds does not take a second inside it.
                */}
                  <div className="space-y-2 pt-3">
                    {workout.holdings.map((holding, index) => (
                      <div
                        key={index}
                        role="group"
                        aria-label={
                          holding.kind === 'exercise'
                            ? holding.exercise.name
                            : GROUP_LABEL[holding.group]
                        }
                        className="rounded-2xl bg-surface-raised p-4"
                      >
                        {holding.kind === 'exercise' ? (
                          <HeldExerciseRow exercise={holding.exercise} />
                        ) : (
                          <>
                            <p
                              className={`flex items-center gap-2 text-xs font-semibold tracking-wider uppercase ${GROUP_TEXT[holding.group]}`}
                            >
                              <GroupIcon kind={holding.group} className="size-4" />
                              {GROUP_LABEL[holding.group]}
                            </p>
                            <div className="space-y-3 pt-3">
                              {holding.exercises.map((exercise, within) => (
                                <HeldExerciseRow key={within} exercise={exercise} />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {/*
        What you have done of it. Said only once the counts have arrived: "nothing yet" from a
        page still waiting on the answer is a claim about somebody's training, and a wrong one.
      */}
        <section aria-label="Your runs" className="pt-10">
          <h2 className="pb-4 text-lg font-medium text-accent">Your runs</h2>
          {done === null && !countsMissed ? (
            <SkeletonBlock label="Loading what you have done of it" className="space-y-2">
              <Skeleton className="h-5 w-56" />
              <Skeleton className="h-16 w-full rounded-2xl" />
            </SkeletonBlock>
          ) : runs.length === 0 ? (
            <p className="text-ink-muted">You have not run this yet.</p>
          ) : (
            <ul className="space-y-4">
              {runs.map((run) => (
                <li key={run.id} className="flex items-start gap-3">
                  {/*
                  The one still going wears a filled mark and the ones behind it hollow ones, so
                  a glance down the column says where you are in this program's history without
                  reading a date.
                */}
                  <span
                    aria-hidden
                    className={`mt-2 size-2.5 shrink-0 rounded-full ${
                      run.endedAt === null ? 'bg-accent' : 'bg-ink-muted/40'
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <p>{runSpan(run)}</p>
                    <p
                      className={`text-sm ${run.endedAt === null ? 'text-accent' : 'text-ink-muted'}`}
                    >
                      {runDetail(run)}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm text-ink-muted">
                    {runTally(run.workoutCount ?? 0)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </section>

      {/*
        The shelf the page's one action stands on: start it, and a menu holding everything
        rarer. It stays at the foot of the card however far the page has been scrolled, because
        starting a program is what the page is for and reading to the end of somebody's training
        history is not a way to be offered it.

        It floats over the page rather than ending it: the card is what it is positioned
        against, so the page runs on underneath and fades out into the shelf's own ground
        instead of stopping at a rule drawn across it. The page keeps the shelf's height clear
        at its foot, so nothing it holds is ever left under there.

        Only the row answers a press. The fade above it is a picture of the page going quiet,
        not a lid over the last of it.
      */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-surface-overlay via-surface-overlay via-60% to-transparent px-4 pt-10 pb-3">
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            // The one you are on is not one to start: starting it again is a fresh run with its
            // progressions reset, which is a decision to make from the run rather than by
            // pressing on.
            disabled={onItNow}
            className="h-12 flex-1 rounded-full bg-accent font-medium text-on-accent transition-opacity hover:opacity-90 disabled:cursor-default disabled:opacity-40 disabled:hover:opacity-40"
            onClick={() => {
              /*
               * Starting ends the run underway, so it asks while there is one to end; a strict
               * schedule has to be asked which weekday its cycle begins on either way.
               */
              if (running === null && strictNode(facts.script) === null) {
                void start(null).catch(() => {
                  setTrouble('Starting it did not work.')
                })
              } else {
                setStarting(true)
              }
            }}
          >
            {onItNow ? "You're on this program" : 'Start this program'}
          </button>
          <Menu
            label="Template options"
            size="size-11"
            icon={<MoreIcon aria-hidden className="size-6" />}
          >
            {held !== null ? (
              <MenuItem
                icon={<EditIcon aria-hidden className="size-5" />}
                onSelect={() => {
                  void navigate({
                    to: '/templates/$templateId/edit',
                    params: { templateId: facts.templateId },
                  })
                }}
              >
                Edit template
              </MenuItem>
            ) : (
              <MenuItem
                icon={<CopyIcon aria-hidden className="size-5" />}
                onSelect={() => {
                  void copy().catch(() => {
                    setTrouble('Copying it did not work.')
                  })
                }}
              >
                Copy to your library
              </MenuItem>
            )}
            {held !== null ? (
              <MenuItem
                danger
                icon={<DeleteIcon aria-hidden className="size-5" />}
                onSelect={() => {
                  setDeleting(true)
                }}
              >
                Delete template
              </MenuItem>
            ) : null}
            {/*
            An admin can take anything out of Explore. It is the author's own unpublish under
            another name, so it asks the same way and leaves every run of it alone.
          */}
            {admin && !facts.mine && facts.published ? (
              <MenuItem
                danger
                icon={<UnpublishIcon aria-hidden className="size-5" />}
                onSelect={() => {
                  setUnlisting(true)
                }}
              >
                Unlist
              </MenuItem>
            ) : null}
          </Menu>
        </div>
      </div>

      {deleting && held !== null ? (
        <ConfirmDialog
          title={`Delete ${facts.name}?`}
          body="The template goes. The runs of it stay in your history, and so do the workouts you logged from it."
          confirm="Delete"
          onConfirm={() => {
            setDeleting(false)
            const named = facts.name
            /*
             * Leaving is decided before the template goes, not after. The library is written
             * first and the navigation is its own await, so a page that waited for both would
             * render a frame with nothing to be about — and answer a deliberate delete with
             * "this template is not yours, and it is not in Explore."
             */
            setLeaving(true)
            void service
              .deleteTemplate(held.id)
              .then(() => {
                goBack()
                // Said over the page it lands on, which is where somebody can read it.
                toast(`${named} deleted`)
              })
              .catch(() => {
                setLeaving(false)
                setTrouble('Deleting needs a connection.')
              })
          }}
          onDismiss={() => {
            setDeleting(false)
          }}
        />
      ) : null}

      {unlisting ? (
        <ConfirmDialog
          title={`Unlist ${facts.name}?`}
          body="It leaves Explore, and nobody new can start it. Everybody already running it carries on, on the version they are on."
          confirm="Unlist"
          onConfirm={async () => {
            await reached(
              () => service.unpublishTemplate(facts.templateId),
              'Unlisting needs a connection.',
            )
            setListing(null)
            setUnlisting(false)
          }}
          onDismiss={() => {
            setUnlisting(false)
          }}
        />
      ) : null}

      {starting ? (
        <StartRunDialog
          name={facts.name}
          ending={running === null ? null : running.templateName || 'The run you are on now'}
          strict={strictNode(facts.script)}
          onStart={(anchorDay) => {
            setStarting(false)
            void start(anchorDay).catch(() => {
              setTrouble('Starting it did not work.')
            })
          }}
          onDismiss={() => {
            setStarting(false)
          }}
        />
      ) : null}
    </>
  )
}

/** One of the numbers beside the page's name, over the word for what it counts. */
function Stat({ value, label }: { value: string; label: string }) {
  return (
    // Grouped and named, so the number and the word for what it counts are read as one thing
    // rather than as a stray digit followed by a stray noun.
    <div role="group" aria-label={label}>
      <p className="text-lg leading-tight font-medium">{value}</p>
      <p className="text-[0.6875rem] tracking-wider text-ink-muted uppercase">{label}</p>
    </div>
  )
}

/** One exercise, and what its lines ask of it — the same reading the editor's rows carry. */
function HeldExerciseRow({ exercise }: { exercise: HeldExercise }) {
  return (
    <div className="flex gap-3">
      <span aria-hidden className="mt-2.5 size-1.5 shrink-0 rounded-full bg-block-exercise" />
      <div className="min-w-0">
        <p>{exercise.name}</p>
        {exercise.lines.map((line, index) => (
          <p key={index} className="text-sm text-ink-muted">
            {line}
          </p>
        ))}
      </div>
    </div>
  )
}
